/**
 * Real auth (Push: customer portal). Replaces the v1 stub.
 *
 * Login posts credentials to mttr/app_api (`/api/login`), which verifies them
 * locally against the imported directory and returns a signed token + user.
 * The token + user are persisted to localStorage so a refresh stays logged in;
 * on mount we restore them and validate the token via `/api/me` (logging out
 * if it's expired/invalid). Role (`platform` | `customer` | `vendor`) drives
 * the operator-vs-customer view split.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  type AuthUser,
  type Role,
  fetchMe,
  login as apiLogin,
} from "../services/authApi";
import { setDisplayTimezone } from "../lib/format";

export type { Role } from "../services/authApi";

export interface User {
  user_id: number;
  username: string;
  display_name: string;
  email: string | null;
  role: Role;
  company_id: number | null;
  company_name: string | null;
  is_super_user: boolean;
  /** MTTR RBAC capability keys (e.g. "minmax.edit", "users.manage"). */
  capabilities: string[];
  /** Company web-display timezone (IANA); null = UTC. */
  display_timezone: string | null;
}

export interface AuthState {
  user: User | null;
  role: Role | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
  /** MTTR RBAC capabilities for the current user (empty when logged out). */
  capabilities: string[];
  /** True if the current user holds `capability`. */
  can: (capability: string) => boolean;
  /** True on a SANITISED session: prices are withheld and part numbers, part
   *  descriptions and work-center names arrive as stable pseudonyms
   *  (`PART-418823`, `Work Center 214`). Demo Viewer accounts, and anyone a
   *  platform admin has denied `data.unmasked`.
   *
   *  The server does the masking -- this flag only exists so the UI can SAY
   *  so. Without it a withheld price renders as blank and reads like missing
   *  data, which someone would reasonably report as a bug. Never use it to
   *  decide what to hide: by the time a payload reaches the client it has
   *  already been sanitised. */
  masked: boolean;
}

const TOKEN_KEY = "mttr_token";
const USER_KEY = "mttr_user";

function toUser(u: AuthUser, identifier?: string): User {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return {
    user_id: u.user_id,
    username: identifier || u.email || String(u.user_id),
    display_name: name || u.email || identifier || `User ${u.user_id}`,
    email: u.email,
    role: u.role,
    company_id: u.company_id,
    company_name: u.company_name,
    is_super_user: u.is_super_user,
    capabilities: u.capabilities ?? [],
    display_timezone: u.display_timezone ?? null,
  };
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Apply the user's company display timezone to all rendered timestamps
  // (fmtUtc reads it). Runs on login, session restore, /api/me refresh, and
  // logout (-> null = UTC).
  useEffect(() => {
    setDisplayTimezone(user?.display_timezone ?? null);
  }, [user]);

  // Restore a persisted session on mount, then validate the token.
  useEffect(() => {
    let cancelled = false;
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);
    if (!storedToken || !storedUser) {
      setIsLoading(false);
      return;
    }
    try {
      setUser(JSON.parse(storedUser) as User);
      setToken(storedToken);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setIsLoading(false);
      return;
    }
    // Validate against the backend; clear if expired/invalid. On success,
    // refresh capabilities + super-user from /api/me (covers a localStorage
    // user persisted before capabilities existed) without losing identity.
    fetchMe(storedToken)
      .then((fresh) => {
        if (cancelled) return;
        setUser((prev) => {
          if (!prev) return prev;
          const merged: User = {
            ...prev,
            is_super_user: fresh.is_super_user,
            capabilities: fresh.capabilities ?? prev.capabilities,
            display_timezone: fresh.display_timezone ?? prev.display_timezone,
          };
          localStorage.setItem(USER_KEY, JSON.stringify(merged));
          return merged;
        });
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setToken(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (identifier: string, password: string): Promise<void> => {
      const res = await apiLogin(identifier, password);
      const u = toUser(res.user, identifier);
      localStorage.setItem(TOKEN_KEY, res.token);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      setToken(res.token);
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    setIsLoggingOut(true);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setToken(null);
    setIsLoggingOut(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      role: user?.role ?? null,
      token,
      isAuthenticated: !!token && !!user,
      isLoading,
      login,
      logout,
      isLoggingOut,
      capabilities: user?.capabilities ?? [],
      can: (capability: string) => (user?.capabilities ?? []).includes(capability),
      // Note the polarity: `data.unmasked` is the permission to see REAL
      // values, so masking is the ABSENCE of it. Logged out reads as masked,
      // which is the safe side of a flag that only ever adds a caveat.
      masked: !(user?.capabilities ?? []).includes("data.unmasked"),
    }),
    [user, token, isLoading, isLoggingOut, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
